import { useEffect, useMemo, useState } from "react";
import { http, fmtNum } from "@/lib/api";

const CLASS_STYLES = {
    Fast: "text-emerald-600 bg-emerald-500/10 border-emerald-500/30",
    Slow: "text-amber-600 bg-amber-500/10 border-amber-500/30",
    NonMoving: "text-orange-600 bg-orange-500/10 border-orange-500/30",
    Dead: "text-red-600 bg-red-500/10 border-red-500/30",
};

export default function Inventory() {
    const [rows, setRows] = useState([]);
    const [summary, setSummary] = useState(null);
    const [classFilter, setClassFilter] = useState("All");
    const [q, setQ] = useState("");

    useEffect(() => {
        http.get("/inventory", { params: { limit: 500 } }).then((r) => setRows(r.data));
        http.get("/inventory/summary").then((r) => setSummary(r.data));
    }, []);

    const filtered = useMemo(() => {
        return rows.filter((r) => {
            if (classFilter !== "All" && r.classification !== classFilter) return false;
            if (q) {
                const term = q.toLowerCase();
                return (
                    r.product?.sku?.toLowerCase().includes(term) ||
                    r.product?.name?.toLowerCase().includes(term) ||
                    r.store?.name?.toLowerCase().includes(term)
                );
            }
            return true;
        });
    }, [rows, classFilter, q]);

    return (
        <div className="mx-auto max-w-[1560px] space-y-4 p-6">
            <div className="flex items-end justify-between">
                <div>
                    <div className="mlabel">Inventory</div>
                    <h1 className="text-[22px] font-semibold tracking-tight">Store-SKU Ledger</h1>
                </div>
                <div className="text-[11px] text-muted-foreground">
                    {summary
                        ? `${fmtNum(summary.total_positions)} positions · ${fmtNum(summary.stockouts)} stock-outs · ${fmtNum(summary.dead_stock)} dead`
                        : "…"}
                </div>
            </div>

            {/* Summary strip */}
            <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
                <MiniStat label="Positions" value={summary ? fmtNum(summary.total_positions) : "…"} />
                <MiniStat label="Stock-outs" value={summary ? fmtNum(summary.stockouts) : "…"} tone="red" />
                <MiniStat label="Overstock (12w+)" value={summary ? fmtNum(summary.overstock) : "…"} tone="amber" />
                <MiniStat label="Dead" value={summary ? fmtNum(summary.dead_stock) : "…"} tone="red" />
                <MiniStat
                    label="Fast Movers"
                    value={summary ? fmtNum(summary.by_classification?.Fast || 0) : "…"}
                    tone="green"
                />
            </div>

            {/* Filters */}
            <div className="flex items-center gap-2">
                <input
                    data-testid="inv-search"
                    placeholder="Search SKU / product / store…"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    className="w-72 rounded-sm border border-border bg-background px-3 py-1.5 text-[12px] outline-none focus:border-primary/60"
                />
                <div className="flex items-center gap-1 rounded-sm border border-border bg-card p-0.5">
                    {["All", "Fast", "Slow", "NonMoving", "Dead"].map((c) => (
                        <button
                            key={c}
                            onClick={() => setClassFilter(c)}
                            data-testid={`inv-class-${c}`}
                            className={
                                "rounded-sm px-2.5 py-1 text-[11px] font-medium transition-colors " +
                                (classFilter === c
                                    ? "bg-primary text-primary-foreground"
                                    : "text-muted-foreground hover:text-foreground")
                            }
                        >
                            {c}
                        </button>
                    ))}
                </div>
                <div className="ml-auto text-[11px] text-muted-foreground">
                    Showing <span className="mono-num text-foreground">{filtered.length}</span> of {rows.length}
                </div>
            </div>

            {/* Table */}
            <div className="overflow-hidden rounded-sm border border-border bg-card" data-testid="inv-table">
                <div className="max-h-[calc(100vh-320px)] overflow-auto">
                    <table className="w-full text-[12px]">
                        <thead className="sticky top-0 z-10 bg-muted/60 text-[10px] uppercase tracking-wider text-muted-foreground backdrop-blur">
                            <tr>
                                <th className="px-3 py-2 text-left font-semibold">SKU</th>
                                <th className="px-3 py-2 text-left font-semibold">Product</th>
                                <th className="px-3 py-2 text-left font-semibold">Store</th>
                                <th className="px-3 py-2 text-right font-semibold">On Hand</th>
                                <th className="px-3 py-2 text-right font-semibold">In Transit</th>
                                <th className="px-3 py-2 text-right font-semibold">Reserved</th>
                                <th className="px-3 py-2 text-right font-semibold">WoC</th>
                                <th className="px-3 py-2 text-right font-semibold">Age (d)</th>
                                <th className="px-3 py-2 text-left font-semibold">Class</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((r) => (
                                <tr
                                    key={r.id}
                                    data-testid={`inv-row-${r.id}`}
                                    className="border-t border-border transition-colors hover:bg-muted/40"
                                >
                                    <td className="mono-num px-3 py-1.5 font-medium">{r.product?.sku || "—"}</td>
                                    <td className="px-3 py-1.5">{r.product?.name || "—"}</td>
                                    <td className="px-3 py-1.5 text-muted-foreground">
                                        {r.store?.name} · {r.store?.city}
                                    </td>
                                    <td className="mono-num px-3 py-1.5 text-right">{fmtNum(r.on_hand)}</td>
                                    <td className="mono-num px-3 py-1.5 text-right">{fmtNum(r.in_transit)}</td>
                                    <td className="mono-num px-3 py-1.5 text-right">{fmtNum(r.reserved)}</td>
                                    <td className="mono-num px-3 py-1.5 text-right">{r.weeks_of_cover?.toFixed(1)}</td>
                                    <td className="mono-num px-3 py-1.5 text-right">{r.ageing_days}</td>
                                    <td className="px-3 py-1.5">
                                        <span
                                            className={
                                                "rounded-sm border px-1.5 py-[1px] text-[10px] font-medium " +
                                                CLASS_STYLES[r.classification]
                                            }
                                        >
                                            {r.classification}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

function MiniStat({ label, value, tone = "default" }) {
    const t =
        tone === "red"
            ? "text-red-500"
            : tone === "green"
            ? "text-emerald-600"
            : tone === "amber"
            ? "text-amber-500"
            : "text-foreground";
    return (
        <div className="rounded-sm border border-border bg-card p-3">
            <div className="mlabel">{label}</div>
            <div className={`mono-num mt-1 text-xl font-semibold ${t}`}>{value}</div>
        </div>
    );
}
